'use client';

/**
 * src/components/ServiceWorkerRegister.js
 *
 * Registra /sw.js + monitorea updates con UI visible al user.
 *
 * Antes este componente solo registraba el SW y mostraba un console.info
 * cuando había update — el user nunca veía nada y seguía usando bundle
 * viejo aunque el deploy hubiera salido. Inaceptable para SaaS serio.
 *
 * Ahora hace todo lo que un PWA serio debería:
 *
 *   1. Registra /sw.js (vía middleware → /api/sw con SHA inyectado).
 *
 *   2. Detecta updates por DOS canales independientes:
 *      a) SW lifecycle: `updatefound` event cuando el browser detecta un
 *         /sw.js distinto al instalado.
 *      b) Build version polling: fetch a /api/build-version cada 2min y
 *         on visibilitychange. Si el SHA difiere del momento de carga,
 *         disparamos toast.
 *      Doble canal porque a veces el SW update se atasca (browsers
 *      buggy, network glitches) y el side-channel HTTP siempre funciona.
 *
 *   3. Toast persistente con botón "Actualizar ahora":
 *      - Cuando se detecta nueva versión, mostramos un banner fixed-top
 *        con texto + botón. NO se autocierra. El user decide cuándo
 *        recargar (en medio de un chat largo nadie quiere que recargue
 *        sin avisar).
 *      - Click "Actualizar ahora" → postMessage SKIP_WAITING al SW
 *        waiting → cuando activate dispara, el SW manda SW_UPDATED →
 *        cliente hace window.location.reload() limpio.
 *
 *   4. Auto-recarga cuando recibe SW_UPDATED postMessage del SW:
 *      Si el activate del SW vino de un SKIP_WAITING que disparamos
 *      nosotros (botón "Actualizar"), el reload es lo que el user pidió.
 *      Si vino de otra fuente (otra pestaña), igual reload — para
 *      mantener consistencia entre pestañas.
 *
 *   5. Cleanup robusto:
 *      Si el user no actualiza en 30min, le re-mostramos el banner
 *      (suave nudge). Si tiene pestañas viejas con SW viejo, en algún
 *      momento se actualizan vía polling.
 */

import { useEffect, useState, useRef } from 'react';

// FIX (2026-05-11) UX: bajamos polling de 2min → 10min y RE_NUDGE de 30min
// → 4h. Estamos en sprint con deploys frecuentes; los users veían el banner
// cada 2 minutos. Polling más relajado + ventanas más amplias = menos ruido.
const POLL_INTERVAL_MS = 10 * 60 * 1000;  // antes 2min
const RE_NUDGE_MS = 4 * 60 * 60 * 1000;   // antes 30min

// SILENT RELOAD (Opción B 2026-05-11): si detectamos un "momento seguro"
// dentro de los primeros AUTO_RELOAD_WINDOW_MS después de detectar la nueva
// versión, recargamos silenciosamente sin banner.
// Momento seguro = (a) pestaña en background, (b) idle >5min sin interacción,
// (c) ningún input/textarea con texto sin guardar.
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;        // 5min sin interacción
const AUTO_RELOAD_WINDOW_MS = 30 * 60 * 1000;   // ventana donde intentar auto

// Heurística "unsaved input": busca cualquier <input>, <textarea>, o
// [contenteditable] con valor no vacío. Esto cubre los casos críticos
// (textarea del chat con prompt a medio escribir, formulario de evento,
// password reset en /reset-password, etc.).
function hasUnsavedInput() {
  if (typeof document === 'undefined') return false;
  const inputs = document.querySelectorAll('input, textarea, [contenteditable="true"]');
  for (const el of inputs) {
    if (el.type === 'hidden' || el.type === 'button' || el.type === 'submit' || el.type === 'reset') continue;
    // Pass de submit/button explícito.
    const val = el.value !== undefined ? el.value : el.textContent;
    if (val && String(val).trim().length > 0) {
      // Excepción: inputs con autocomplete='off' y valor exacto al placeholder
      // probablemente son state hidratados, no input fresh. Conservadora: si
      // hay texto, consideramos unsaved.
      return true;
    }
  }
  return false;
}

export default function ServiceWorkerRegister() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const initialBuildVersionRef = useRef(null);
  const registrationRef = useRef(null);
  const lastNudgeRef = useRef(0);
  // Tracking para silent reload
  const lastActivityRef = useRef(Date.now());
  const updateDetectedAtRef = useRef(null);
  const silentReloadCheckIntervalRef = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    let mounted = true;
    // ¿La página ya estaba controlada por un SW al cargar? En la PRIMERA
    // visita no: el SW se instala, hace clients.claim() y manda SW_UPDATED,
    // y recargar ahí solo hacía parpadear la página (1-2 recargas en 2 s).
    let habiaControlador = Boolean(navigator.serviceWorker.controller);

    // Track last user interaction para idle detection.
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(evt => {
      window.addEventListener(evt, onActivity, { passive: true });
    });

    // SILENT RELOAD: intenta reload sin banner si hay momento seguro.
    // Devuelve true si recargó (o iba a recargar), false si no era seguro.
    const trySilentReload = () => {
      if (!mounted) return false;
      const now = Date.now();
      const detectedAt = updateDetectedAtRef.current;
      // Solo intentamos auto-reload dentro de la ventana de tolerancia.
      if (!detectedAt || (now - detectedAt) > AUTO_RELOAD_WINDOW_MS) return false;
      // No reload si el user está editando algo.
      if (hasUnsavedInput()) return false;
      // Triggers seguros:
      //   1. Tab está en background (hidden)
      //   2. Usuario lleva >IDLE_THRESHOLD_MS sin interactuar
      const isHidden = document.visibilityState === 'hidden';
      const idleMs = now - lastActivityRef.current;
      const isIdle = idleMs >= IDLE_THRESHOLD_MS;
      if (!isHidden && !isIdle) return false;
      // OK, momento seguro. Reload silencioso.
      // eslint-disable-next-line no-console
      console.info('[PWA] Silent reload (reason:', isHidden ? 'tab hidden' : `idle ${Math.round(idleMs/1000)}s`, ')');
      window.location.reload();
      return true;
    };

    const showUpdateBanner = () => {
      if (!mounted) return;
      // Marcamos el momento de detección para el silent reload window.
      if (updateDetectedAtRef.current === null) {
        updateDetectedAtRef.current = Date.now();
      }
      // Intentar silent reload primero. Si no es momento seguro, fallback a banner.
      if (trySilentReload()) return;
      // Si no, programar checks cada 30s para intentar silent reload
      // antes de mostrar el banner.
      if (!silentReloadCheckIntervalRef.current) {
        silentReloadCheckIntervalRef.current = setInterval(() => {
          if (trySilentReload()) {
            clearInterval(silentReloadCheckIntervalRef.current);
            silentReloadCheckIntervalRef.current = null;
          }
        }, 30 * 1000);
      }
      // Si dentro de la ventana de auto-reload todavía no recargó silenciosamente,
      // recién ahí mostrar el banner.
      const now = Date.now();
      if (lastNudgeRef.current && now - lastNudgeRef.current < RE_NUDGE_MS) {
        return;
      }
      lastNudgeRef.current = now;
      setUpdateAvailable(true);
    };

    // ── Canal 1: SW lifecycle ──
    const registerSW = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });
        registrationRef.current = reg;

        // Si ya hay un SW waiting al cargar, mostrar banner inmediato.
        if (reg.waiting && navigator.serviceWorker.controller) {
          showUpdateBanner();
        }

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // 'installed' + hay controller activo = nuevo SW listo, esperando.
            // (Si no hay controller, es la primera instalación — no hay
            // update que mostrar al user.)
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateBanner();
            }
          });
        });

        // Polling activo del SW: cada 60s pedimos al browser que chequee /sw.js.
        // El browser devuelve 304 si no cambió, dispara updatefound si sí.
        const swPoll = setInterval(() => {
          reg.update().catch(() => {});
        }, 60 * 1000);

        // ── Canal 2: build-version side-channel ──
        // Nos protege de bugs del SW lifecycle (browsers viejos, redes flojas).
        const fetchVersion = async () => {
          try {
            const res = await fetch('/api/build-version', { cache: 'no-store' });
            if (!res.ok) return;
            const data = await res.json();
            if (!data?.version) return;
            if (initialBuildVersionRef.current === null) {
              initialBuildVersionRef.current = data.version;
              return;
            }
            if (data.version !== initialBuildVersionRef.current) {
              showUpdateBanner();
            }
          } catch {
            // Network glitch — silencio, reintenta en el próximo tick.
          }
        };
        await fetchVersion();
        const versionPoll = setInterval(fetchVersion, POLL_INTERVAL_MS);

        // Re-poll cuando el user vuelve a la pestaña — capta deploys que
        // pasaron mientras la pestaña estaba en background.
        const onVisibility = () => {
          if (document.visibilityState === 'visible') {
            fetchVersion();
            reg.update().catch(() => {});
          }
        };
        document.addEventListener('visibilitychange', onVisibility);

        // ── Listener de mensajes del SW ──
        const onMessage = (event) => {
          if (event.data?.type === 'SW_UPDATED') {
            // Primera instalación (no había versión vieja): no es una
            // actualización. Desde ahora la página sí queda controlada.
            if (!habiaControlador) {
              habiaControlador = true;
              return;
            }
            // El SW activó una versión nueva.
            // Si el user no tiene nada escrito → reload silencioso inmediato.
            // Si tiene texto en el input → mostramos banner para no interrumpir.
            if (!hasUnsavedInput()) {
              window.location.reload();
            } else {
              showUpdateBanner();
            }
          }
        };
        navigator.serviceWorker.addEventListener('message', onMessage);

        return () => {
          clearInterval(swPoll);
          clearInterval(versionPoll);
          document.removeEventListener('visibilitychange', onVisibility);
          navigator.serviceWorker.removeEventListener('message', onMessage);
        };
      } catch (e) {
        // SW falló registro. La app sigue funcionando sin SW.
        // No spamear con error ruidoso — el side-channel HTTP de
        // build-version igual va a detectar updates eventualmente.
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[PWA] SW register falló:', e?.message);
        }
      }
    };

    let cleanup;
    registerSW().then((c) => { cleanup = c; });

    return () => {
      mounted = false;
      if (cleanup) cleanup();
      // Cleanup silent reload interval + activity listeners
      if (silentReloadCheckIntervalRef.current) {
        clearInterval(silentReloadCheckIntervalRef.current);
        silentReloadCheckIntervalRef.current = null;
      }
      ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(evt => {
        window.removeEventListener(evt, onActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateClick = () => {
    setIsUpdating(true);
    const reg = registrationRef.current;
    if (reg?.waiting) {
      // SW nuevo está en estado waiting → le pedimos que skipee.
      // Cuando activate dispara, manda SW_UPDATED → onMessage hace reload.
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Fallback: si el SW no responde en 3s, recargamos igual.
      // (La app cargará el bundle nuevo aunque el SW no haya activado.)
      setTimeout(() => {
        if (typeof window !== 'undefined') window.location.reload();
      }, 3000);
    } else {
      // Sin SW waiting (caso side-channel detection sin SW lifecycle)
      // → reload directo. El SW se actualizará en el próximo lifecycle.
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
    // El banner se va a re-mostrar después de RE_NUDGE_MS o en próxima
    // detección. Pero si el user explícitamente cerró, le damos margen.
    lastNudgeRef.current = Date.now();
  };

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        background: 'linear-gradient(135deg, #B8860B 0%, #8B6508 100%)',
        color: '#fff',
        padding: '0.85rem 1.25rem',
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
        fontSize: '0.9rem',
        fontWeight: 500,
        maxWidth: 'calc(100vw - 40px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <span style={{ fontSize: '1.1rem' }}>✨</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700 }}>Nueva versión disponible</div>
        <div style={{ fontSize: '0.78rem', opacity: 0.9 }}>
          Actualiza para recibir las últimas mejoras y bug fixes.
        </div>
      </div>
      <button
        onClick={handleUpdateClick}
        disabled={isUpdating}
        style={{
          background: '#fff',
          color: '#B8860B',
          border: 'none',
          padding: '0.55rem 1rem',
          borderRadius: 8,
          fontSize: '0.85rem',
          fontWeight: 700,
          cursor: isUpdating ? 'not-allowed' : 'pointer',
          opacity: isUpdating ? 0.6 : 1,
          fontFamily: 'inherit',
          flexShrink: 0,
        }}
      >
        {isUpdating ? 'Actualizando…' : 'Actualizar ahora'}
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Descartar"
        style={{
          background: 'transparent',
          color: '#fff',
          border: 'none',
          fontSize: '1.3rem',
          cursor: 'pointer',
          padding: '0 0.25rem',
          lineHeight: 1,
          opacity: 0.8,
        }}
      >
        ×
      </button>
    </div>
  );
}
